/**
 * Service layer for the enriched-product CSV export lifecycle. Plain async
 * fetchers over `exportClient` (`/api/v1/export/v1/exports/**`) returning the
 * contract DTOs verbatim. The SSE status stream lives in `export-status-stream.ts`.
 */

import { exportClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';

import type {
  ExportArchiveResult,
  ExportBulkArchiveRequest,
  ExportConfigSnapshot,
  ExportCountsParams,
  ExportCountsResponse,
  ExportDownloadUrlResponse,
  ExportEstimateResponse,
  ExportListItem,
  ExportListParams,
  ExportStatusResponse,
  ExportTemplateConfig,
  ExportTemplateBrowse,
  ExportTemplateBrowseRequest,
  ExportTemplatePreview,
  ExportTemplatePreviewRequest,
  ExportTriggerRequest,
  ExportTriggerResponse,
} from '../types/export.types';

/** Dry-run: count products + estimate file size for a filter set, no workflow started. */
export async function estimateExport(
  payload: ExportTriggerRequest,
): Promise<ExportEstimateResponse> {
  return exportClient
    .post(endpoints.exports.estimate(), { json: payload })
    .json<ExportEstimateResponse>();
}

/** Start an export. Returns the exportId that keys the rest of the lifecycle. */
export async function triggerExport(payload: ExportTriggerRequest): Promise<ExportTriggerResponse> {
  return exportClient
    .post(endpoints.exports.trigger(), { json: payload })
    .json<ExportTriggerResponse>();
}

/** One-shot status read (mints a fresh 48h downloadUrl on COMPLETED). Used as the SSE fallback. */
export async function getExportStatus(exportId: string): Promise<ExportStatusResponse> {
  return exportClient.get(endpoints.exports.status(exportId)).json<ExportStatusResponse>();
}

/** List the subsidiary's exports, newest first. Filterable by source run + status. */
/** The configuration an export ran with; `null` when it recorded none (an older export). */
export async function getExportConfig(exportId: string): Promise<ExportConfigSnapshot | null> {
  try {
    return await exportClient.get(endpoints.exports.config(exportId)).json<ExportConfigSnapshot>();
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) return null;
    throw error;
  }
}

export async function listExports(params?: ExportListParams): Promise<ExportListItem[]> {
  const searchParams: Record<string, string | number> = {};
  if (params?.sourceWorkflowId) searchParams.sourceWorkflowId = params.sourceWorkflowId;
  if (params?.status) searchParams.status = params.status;
  if (params?.createdFrom) searchParams.createdFrom = params.createdFrom;
  if (params?.createdTo) searchParams.createdTo = params.createdTo;
  if (params?.limit != null) searchParams.limit = params.limit;
  return exportClient.get(endpoints.exports.list(), { searchParams }).json<ExportListItem[]>();
}

/**
 * The unified Catalog exports list read — `sort`/`order`/`cursor`/`archived`/`q` on top of
 * `listExports`'s params. The next cursor comes back on the `X-Next-Cursor` response header
 * (never in the body), so this returns both.
 */
export async function browseExports(
  params: ExportListParams,
): Promise<{ items: ExportListItem[]; nextCursor: string | null }> {
  const searchParams: Record<string, string | number | boolean> = {};
  if (params.sourceWorkflowId) searchParams.sourceWorkflowId = params.sourceWorkflowId;
  if (params.status) searchParams.status = params.status;
  if (params.createdFrom) searchParams.createdFrom = params.createdFrom;
  if (params.createdTo) searchParams.createdTo = params.createdTo;
  if (params.limit != null) searchParams.limit = params.limit;
  if (params.sort) searchParams.sort = params.sort;
  if (params.order) searchParams.order = params.order;
  if (params.cursor) searchParams.cursor = params.cursor;
  if (params.archived != null) searchParams.archived = params.archived;
  if (params.q) searchParams.q = params.q;
  const response = await exportClient.get(endpoints.exports.list(), { searchParams });
  const items = await response.json<ExportListItem[]>();
  return { items, nextCursor: response.headers.get('X-Next-Cursor') };
}

/**
 * Exact KPI/tab counts for the Catalog exports list, scoped by the same `archived`/`q` filters
 * `browseExports` takes. In progress on `aes-delivery` — 404s until deployed; the caller
 * (`use-exports-counts.ts`) falls back to a windowed client computation over the loaded rows.
 */
export async function getExportCounts(params: ExportCountsParams): Promise<ExportCountsResponse> {
  const searchParams: Record<string, string | boolean> = {};
  if (params.sourceWorkflowIdPrefix)
    searchParams.sourceWorkflowIdPrefix = params.sourceWorkflowIdPrefix;
  if (params.archived != null) searchParams.archived = params.archived;
  if (params.createdFrom) searchParams.createdFrom = params.createdFrom;
  return exportClient
    .get(endpoints.exports.counts(), { searchParams })
    .json<ExportCountsResponse>();
}

/** Hide one export from the main list. Idempotent — `archived: 0` if already archived. */
export async function archiveExport(exportId: string): Promise<ExportArchiveResult> {
  return exportClient.post(endpoints.exports.archive(exportId)).json<ExportArchiveResult>();
}

export async function restoreExport(exportId: string): Promise<ExportArchiveResult> {
  return exportClient.post(endpoints.exports.restore(exportId)).json<ExportArchiveResult>();
}

/** Bulk archive/restore — capped at 500 ids per the service's own doc. */
export async function bulkArchiveExports(ids: string[]): Promise<ExportArchiveResult> {
  return exportClient
    .post(endpoints.exports.bulkArchive(), { json: { ids } satisfies ExportBulkArchiveRequest })
    .json<ExportArchiveResult>();
}

export async function bulkRestoreExports(ids: string[]): Promise<ExportArchiveResult> {
  return exportClient
    .post(endpoints.exports.bulkRestore(), { json: { ids } satisfies ExportBulkArchiveRequest })
    .json<ExportArchiveResult>();
}

/** Signal a running export to cancel. 204 No Content; 409 if already terminal. */
export async function cancelExport(exportId: string): Promise<void> {
  await exportClient.post(endpoints.exports.cancel(exportId));
}

/** Delete a terminal export (DB row + GCS blob). 409 if still PENDING/IN_PROGRESS. */
export async function deleteExport(exportId: string): Promise<void> {
  await exportClient.delete(endpoints.exports.delete(exportId));
}

/** Mint a fresh 48h signed download URL for a COMPLETED export (old link expired). */
export async function refreshExportDownloadUrl(
  exportId: string,
): Promise<ExportDownloadUrlResponse> {
  return exportClient
    .post(endpoints.exports.refreshDownloadUrl(exportId))
    .json<ExportDownloadUrlResponse>();
}

/**
 * The static export-templates config. Tenant-independent and version-stamped —
 * identical for every caller, which is why callers cache it for the session
 * rather than per run.
 *
 * `exportClient`, like the whole lifecycle above: every export route is served
 * by agentic-export-service (`/api/v1/export/**`). This note used to explain a
 * split with workflow-webapp; that split is gone.
 */
export async function getExportTemplatesConfig(): Promise<ExportTemplateConfig> {
  return exportClient.get(endpoints.exportTemplates.config()).json<ExportTemplateConfig>();
}

/**
 * Render the first N products of a run raw and template-encoded, for the
 * pre-trigger preview. Starts no workflow and writes nothing; the resolution
 * path is shared with `POST /trigger`, so the cells here are the cells the
 * export will contain.
 */
export async function previewExportTemplate(
  payload: ExportTemplatePreviewRequest,
): Promise<ExportTemplatePreview> {
  return exportClient
    .post(endpoints.exportTemplates.preview(), { json: payload })
    .json<ExportTemplatePreview>();
}

/**
 * One keyset page of a run's products, rendered as the export would write them.
 *
 * Reads the enrichment table and starts nothing, like the preview, and shares
 * the trigger's resolution path down to the renderer — so a row here is the row
 * the file would contain.
 */
export async function browseExportTemplate(
  payload: ExportTemplateBrowseRequest,
): Promise<ExportTemplateBrowse> {
  return exportClient
    .post(endpoints.exportTemplates.browse(), { json: payload })
    .json<ExportTemplateBrowse>();
}
